import { useEffect, useState } from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  campaigns as campaignsApi,
  products as productsApi,
  prospects as prospectsApi,
} from '../api/client.ts';
import type { ProspectQuery } from '../api/client.ts';
import {
  MATCH_KEY_LABEL,
  PROSPECT_STATUS_LABEL,
  STAGE_HINT,
  STAGE_KEYS,
  STAGE_LABEL,
} from '../api/types.ts';
import type { ImportResult, Prospect, ProspectStage, ProspectStatus } from '../api/types.ts';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Field,
  PageHeader,
  Panel,
  ScoreRing,
  Select,
  Spinner,
  StageRail,
  cx,
} from '../ui/primitives.tsx';
import { useDocumentTitle } from '../ui/Logo.tsx';

/**
 * Prospects — a lista de trabalho, organizada pela etapa da venda (Fase 13).
 *
 * Antes era uma lista só, e quem já tinha sido contatado, quem faltava e quem
 * respondeu apareciam misturados, ordenados por nota. A pergunta do vendedor
 * não é "quem tem a maior nota", é "com quem eu falo agora" — e a resposta
 * depende de quem deve o próximo passo. Daí as etapas: cada uma com a sua
 * ordem e as colunas que importam nela.
 *
 * A importação e o cadastro manual ficam aqui, e não numa tela própria,
 * porque o resultado deles é esta lista (§39).
 */

const PAGE_SIZE = 50;

/** As quatro etapas em que se trabalha todo dia; as outras duas ficam ao lado. */
const WORK_STAGES: ProspectStage[] = ['a_contatar', 'aguardando', 'responderam', 'negociacao'];
const ARCHIVE_STAGES: ProspectStage[] = ['encerrados', 'descartados'];

/** A ordem que faz sentido em cada etapa. */
const DEFAULT_SORT: Record<ProspectStage, NonNullable<ProspectQuery['sort']>> = {
  a_contatar: 'score',
  aguardando: 'contacted',
  responderam: 'replied',
  negociacao: 'replied',
  encerrados: 'recent',
  descartados: 'recent',
};

const SORT_OPTIONS: { value: NonNullable<ProspectQuery['sort']>; label: string; stages?: ProspectStage[] }[] = [
  { value: 'score', label: 'Maior nota' },
  { value: 'contacted', label: 'Espera mais longa', stages: ['aguardando'] },
  { value: 'replied', label: 'Resposta mais recente', stages: ['responderam', 'negociacao'] },
  { value: 'recent', label: 'Cadastro mais recente' },
  { value: 'name', label: 'Nome' },
];

function isStage(value: string | null): value is ProspectStage {
  return value !== null && (STAGE_KEYS as string[]).includes(value);
}

export function Prospects() {
  useDocumentTitle('Prospects');
  // Etapa e campanha moram na URL: o filtro sobrevive a um F5 e pode ser
  // copiado para outra pessoa ("olha os que responderam da campanha X").
  const [searchParams, setSearchParams] = useSearchParams();
  const stageParam = searchParams.get('etapa');
  const stage: ProspectStage = isStage(stageParam) ? stageParam : 'a_contatar';
  const campaignId = searchParams.get('campaignId') ?? '';

  // Busca, cidade e nota também moram na URL: recarregar ou mandar o link
  // para alguém mantém o recorte. O que se digita espera 300 ms antes de virar
  // consulta — antes, cada tecla disparava uma requisição.
  const search = searchParams.get('busca') ?? '';
  const city = searchParams.get('cidade') ?? '';
  const minScoreParam = searchParams.get('nota');
  const minScore = minScoreParam && Number.isFinite(Number(minScoreParam)) ? Number(minScoreParam) : undefined;
  const [searchText, setSearchText] = useState(search);
  const [cityText, setCityText] = useState(city);
  const [filters, setFilters] = useState<Pick<ProspectQuery, 'limit' | 'offset'>>({
    limit: PAGE_SIZE,
    offset: 0,
  });
  const [sortByStage, setSortByStage] = useState<Partial<Record<ProspectStage, ProspectQuery['sort']>>>({});
  const sort = sortByStage[stage] ?? DEFAULT_SORT[stage];
  const [panel, setPanel] = useState<'none' | 'manual' | 'import'>('none');

  const query: ProspectQuery = {
    ...filters,
    search: search || undefined,
    city: city || undefined,
    minScore,
    stage,
    sort,
    campaignId: campaignId || undefined,
  };

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['prospects', query],
    queryFn: () => prospectsApi.list(query),
    // Mantém a página anterior visível enquanto a nova carrega: sem isto, cada
    // tecla digitada na busca faz a tabela sumir e voltar.
    placeholderData: (previous) => previous,
  });
  const summary = useQuery({
    queryKey: ['prospects', 'summary', campaignId],
    queryFn: () => prospectsApi.summary(campaignId || undefined),
  });
  const { data: campaignsData } = useQuery({ queryKey: ['campaigns'], queryFn: () => campaignsApi.list() });

  function setParam(key: string, value: string | undefined) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
    setFilters((current) => ({ ...current, offset: 0 }));
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchText.trim() !== search) setParam('busca', searchText.trim() || undefined);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchText]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (cityText.trim() !== city) setParam('cidade', cityText.trim() || undefined);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [cityText]);

  function clearFilters() {
    const next = new URLSearchParams(searchParams);
    for (const key of ['busca', 'cidade', 'nota']) next.delete(key);
    setSearchParams(next, { replace: true });
    setSearchText('');
    setCityText('');
    setFilters({ limit: PAGE_SIZE, offset: 0 });
  }

  const counts = summary.data?.byStage;
  const total = data?.total ?? 0;
  const offset = filters.offset ?? 0;
  const campaignName = campaignsData?.campaigns.find((campaign) => campaign.id === campaignId)?.name;
  const hasFilter = Boolean(search || city || minScore);

  return (
    <>
      <PageHeader
        title="Prospects"
        description={
          campaignName
            ? `Empresas da campanha ${campaignName}, pela etapa da venda.`
            : 'Todas as empresas do perfil, pela etapa da venda.'
        }
        actions={
          <>
            <Button onClick={() => setPanel((open) => (open === 'manual' ? 'none' : 'manual'))}>
              {panel === 'manual' ? 'Fechar cadastro' : 'Adicionar empresa'}
            </Button>
            <Button variant="primary" onClick={() => setPanel((open) => (open === 'import' ? 'none' : 'import'))}>
              {panel === 'import' ? 'Fechar importação' : 'Importar CSV'}
            </Button>
          </>
        }
      />

      {panel !== 'none' && (
        <div className="enter-up mb-6">
          {panel === 'manual' ? <ManualLeadPanel /> : <ImportPanel onImported={() => setPanel('none')} />}
        </div>
      )}

      <div className="mb-4 flex flex-col gap-3">
        <StageRail
          label="Etapas da venda"
          active={stage}
          onSelect={(key) => setParam('etapa', key === 'a_contatar' ? undefined : key)}
          stages={WORK_STAGES.map((key) => ({ key, label: STAGE_LABEL[key], count: counts?.[key] }))}
        />
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
          <p className="text-sm text-[var(--color-ink-soft)]">{STAGE_HINT[stage]}</p>
          <div className="flex items-center gap-1">
            {ARCHIVE_STAGES.map((key) => (
              <button
                key={key}
                type="button"
                aria-current={stage === key ? 'page' : undefined}
                onClick={() => setParam('etapa', key)}
                className={cx(
                  'rounded-full px-3 py-1 text-sm transition-colors',
                  stage === key
                    ? 'bg-[var(--color-brand-soft)] text-[var(--color-ink)] shadow-[inset_0_0_0_1px_rgb(10_92_255/0.45)]'
                    : 'text-[var(--color-ink-soft)] hover:bg-[var(--color-hover)] hover:text-[var(--color-ink)]',
                )}
              >
                {STAGE_LABEL[key]} <span className="tabular opacity-70">{counts?.[key] ?? ''}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <Panel flush>
        <div className="grid gap-3 px-5 pb-4 sm:grid-cols-2 lg:grid-cols-[1.3fr_1.3fr_1fr_0.7fr_1.1fr]">
          <Select
            label="Campanha"
            id="prospect-campanha"
            value={campaignId}
            onChange={(value) => setParam('campaignId', value || undefined)}
            options={[
              { value: '', label: 'Todas as campanhas' },
              ...(campaignsData?.campaigns ?? []).map((campaign) => ({ value: campaign.id, label: campaign.name })),
            ]}
          />
          <Field
            label="Buscar"
            id="prospect-busca"
            placeholder="Nome da empresa"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Field
            label="Cidade"
            id="prospect-cidade"
            placeholder="João Pessoa"
            value={cityText}
            onChange={(e) => setCityText(e.target.value)}
          />
          <Field
            label="Nota mínima"
            id="prospect-score"
            type="number"
            min={0}
            max={100}
            value={minScore ?? ''}
            onChange={(e) => setParam('nota', e.target.value === '' ? undefined : e.target.value)}
          />
          <Select
            label="Ordenar por"
            id="prospect-ordem"
            value={sort ?? 'score'}
            onChange={(value) => {
              setSortByStage((current) => ({ ...current, [stage]: value as ProspectQuery['sort'] }));
              setFilters((current) => ({ ...current, offset: 0 }));
            }}
            options={SORT_OPTIONS.filter((option) => !option.stages || option.stages.includes(stage))}
          />
        </div>

        {isLoading && (
          <div className="flex justify-center py-10 text-[var(--color-ink-faint)]">
            <Spinner label="Carregando prospects" />
          </div>
        )}
        {error && (
          <div className="px-5 pb-5">
            <Alert>{error instanceof ApiError ? error.message : 'Não foi possível carregar os prospects.'}</Alert>
          </div>
        )}

        {data && data.prospects.length === 0 && (
          <div className="border-t border-[var(--color-line)] px-5">
            <EmptyState
              title={hasFilter ? 'Nenhuma empresa com esses filtros' : `Nenhuma empresa em “${STAGE_LABEL[stage]}”`}
              description={
                hasFilter
                  ? 'Limpe a busca, a cidade ou a nota mínima para ver a etapa inteira.'
                  : stage === 'a_contatar'
                    ? 'Importe um CSV numa campanha, colete pelo Google Maps ou cadastre uma empresa que você encontrou.'
                    : 'Quando uma empresa chegar a esta etapa, ela aparece aqui.'
              }
              action={
                hasFilter ? (
                  <Button onClick={clearFilters}>Limpar filtros</Button>
                ) : stage === 'a_contatar' ? (
                  <Button variant="primary" onClick={() => setPanel('import')}>
                    Importar CSV
                  </Button>
                ) : undefined
              }
            />
          </div>
        )}

        {data && data.prospects.length > 0 && (
          <>
            <ProspectTable prospects={data.prospects} stage={stage} dimmed={isFetching} />
            <Pagination
              offset={offset}
              total={total}
              onChange={(next) => setFilters((current) => ({ ...current, offset: next }))}
            />
          </>
        )}
      </Panel>
    </>
  );
}

const EMPTY_MANUAL = {
  campaignId: '',
  productId: '',
  googleUrl: '',
  instagram: '',
  website: '',
  name: '',
  category: '',
  phone: '',
  city: '',
  state: '',
  rating: '',
  reviewCount: '',
  cnpj: '',
  notes: '',
};

/**
 * Cadastro de uma empresa encontrada à mão (decisão 35).
 *
 * O texto do topo diz o que o sistema faz e o que NÃO faz com os links, de
 * propósito: quem cola um Instagram espera que o sistema "leia o perfil", e
 * descobrir que não depois de cadastrar pareceria defeito.
 */
function ManualLeadPanel() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: campaignsData } = useQuery({ queryKey: ['campaigns'], queryFn: () => campaignsApi.list() });
  const { data: productsData } = useQuery({ queryKey: ['products'], queryFn: () => productsApi.list() });
  // Diz ao vendedor, antes de cadastrar, se os links vão ser lidos — e quanto isso custa.
  const { data: costs } = useQuery({ queryKey: ['apify-costs'], queryFn: () => campaignsApi.costs() });
  const apifyOn = Boolean(costs?.enabled);

  const [form, setForm] = useState(EMPTY_MANUAL);
  const [analyze, setAnalyze] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const field = (key: keyof typeof EMPTY_MANUAL) => ({
    value: form[key],
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((current) => ({ ...current, [key]: event.target.value })),
  });

  const mutation = useMutation({
    mutationFn: () => prospectsApi.createManual({ ...form, analyze }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['prospects'] });
      navigate(`/prospects/${result.prospectId}`, { state: { manual: result } });
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : 'Não foi possível cadastrar a empresa.'),
  });

  const ready = (form.campaignId !== '' || form.productId !== '') && (form.name !== '' || form.googleUrl !== '');

  return (
    <Panel title="Adicionar empresa" description="Uma empresa que você encontrou: o sistema lê o que der dos links e qualifica.">
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          mutation.mutate();
        }}
      >
        {apifyOn ? (
          <p className="text-sm text-[var(--color-ink-soft)]">
            Cole os links que você encontrou. Com a coleta do Apify ligada, o sistema{' '}
            <strong>lê a ficha do Google</strong> (telefone, categoria, nota, avaliações) e o{' '}
            <strong>perfil público do Instagram</strong> — cerca de US$ 0,009 por empresa, dentro do teto
            mensal (restam US$ {costs?.remainingUsd.toFixed(2)}). O que você preencher tem prioridade
            sobre a ficha. O site, se houver, também é lido.
          </p>
        ) : (
          <p className="text-sm text-[var(--color-ink-soft)]">
            Cole os links que você encontrou. Com a coleta do Apify <strong>desligada</strong>, o sistema
            não lê a ficha do Google nem o perfil do Instagram: do link do Maps sai o nome; do Instagram,
            o @. O site, se houver, é lido. O resto, preencha com o que você está vendo na tela.
          </p>
        )}

        <div className="grid gap-3 md:grid-cols-2">
          <Select
            label="Campanha"
            id="manual-campanha"
            value={form.campaignId}
            onChange={(value) => setForm((current) => ({ ...current, campaignId: value }))}
            options={[
              { value: '', label: 'Sem campanha' },
              ...(campaignsData?.campaigns ?? []).map((campaign) => ({ value: campaign.id, label: campaign.name })),
            ]}
          />
          <Select
            label="Produto"
            id="manual-produto"
            value={form.productId}
            onChange={(value) => setForm((current) => ({ ...current, productId: value }))}
            options={[
              { value: '', label: form.campaignId ? 'O alvo da campanha' : 'Escolha um produto' },
              ...(productsData?.products ?? []).map((product) => ({ value: product.id, label: product.name })),
            ]}
          />
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <Field
            label="Link do Google Maps / Meu Negócio"
            id="manual-google"
            placeholder="https://www.google.com/maps/place/…"
            {...field('googleUrl')}
          />
          <Field label="Instagram" id="manual-instagram" placeholder="instagram.com/empresa ou @empresa" {...field('instagram')} />
          <Field label="Site" id="manual-site" placeholder="empresa.com.br" {...field('website')} />
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <Field
            label="Nome da empresa"
            id="manual-nome"
            hint="Pode ficar vazio se o link do Maps trouxer o nome (link curto não traz)."
            {...field('name')}
          />
          <Field label="Categoria" id="manual-categoria" placeholder="Hamburgueria, Moda, Clínica…" {...field('category')} />
          <Field label="Telefone" id="manual-telefone" placeholder="(83) 99999-9999" {...field('phone')} />
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Field label="Cidade" id="manual-cidade" {...field('city')} />
          <Field label="UF" id="manual-uf" maxLength={2} {...field('state')} />
          <Field label="Nota no Google" id="manual-nota" type="number" step="0.1" min="0" max="5" {...field('rating')} />
          <Field label="Avaliações no Google" id="manual-avaliacoes" type="number" min="0" {...field('reviewCount')} />
          <Field
            label="CNPJ"
            id="manual-cnpj"
            inputMode="numeric"
            placeholder="00.000.000/0000-00"
            hint="Opcional: traz porte e idade da empresa."
            {...field('cnpj')}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="manual-notas" className="text-xs font-medium text-[var(--color-ink-soft)]">
            Informações que você recebeu
          </label>
          <textarea
            id="manual-notas"
            rows={4}
            maxLength={4000}
            placeholder="Ex.: segundo um cliente, os pedidos chegam todos pelo WhatsApp e demoram a ser respondidos."
            className="rounded-[var(--radius-sm)] border border-[var(--color-line-strong)] bg-[var(--color-surface)] px-3 py-2 focus:outline-none focus-visible:border-[var(--color-brand)] text-sm"
            {...field('notes')}
          />
          <span className="text-xs text-[var(--color-ink-faint)]">
            Vai para a análise da IA marcado como não verificado. Só sobre a empresa — nada de dado
            pessoal.
          </span>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={analyze} onChange={(event) => setAnalyze(event.target.checked)} />
          Analisar com IA se a empresa passar na qualificação (chamada paga)
        </label>

        {error && <Alert>{error}</Alert>}

        <div className="flex gap-2">
          <Button type="submit" variant="primary" loading={mutation.isPending} disabled={!ready}>
            Cadastrar e prospectar
          </Button>
          <Button type="button" variant="ghost" onClick={() => setForm(EMPTY_MANUAL)}>
            Limpar
          </Button>
        </div>
      </form>
    </Panel>
  );
}

// --- Tabela -----------------------------------------------------------------

/** "hoje", "ontem", "há 5 dias" — a espera em dias é o que decide o follow-up. */
function since(value: string | null | undefined): string {
  if (!value) return '—';
  const days = Math.floor((Date.now() - new Date(value).getTime()) / 86_400_000);
  if (days <= 0) return 'hoje';
  if (days === 1) return 'ontem';
  return `há ${days} dias`;
}

/** Data de retomada ("AAAA-MM-DD") em relação a hoje. */
function followUpLabel(date: string | null | undefined): { text: string; late: boolean } | undefined {
  if (!date) return undefined;
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  const [, m, d] = date.split('-');
  const text = `${d}/${m}`;
  if (date < today) return { text: `atrasado desde ${text}`, late: true };
  if (date === today) return { text: 'hoje', late: true };
  return { text: `em ${text}`, late: false };
}

interface Column {
  key: string;
  label: string;
  align?: 'right';
  /** Some no celular: nome e o que decide a ação ficam. */
  wide?: boolean;
  render: (prospect: Prospect) => ReactNode;
}

function columnsFor(stage: ProspectStage): Column[] {
  const place: Column = {
    key: 'local',
    label: 'Cidade',
    wide: true,
    render: (p) => (
      <span className="text-[var(--color-ink-soft)]">
        {p.city ?? '—'}
        {p.state ? `/${p.state}` : ''}
      </span>
    ),
  };
  const score: Column = {
    key: 'nota',
    label: 'Nota',
    align: 'right',
    render: (p) => (
      <span className="inline-flex justify-end">
        <ScoreRing value={p.final_score ?? p.technical_score} size={32} />
      </span>
    ),
  };
  const status: Column = { key: 'status', label: 'Situação', render: (p) => <StatusBadge status={p.status} /> };

  switch (stage) {
    case 'a_contatar':
      return [
        place,
        { key: 'categoria', label: 'Categoria', wide: true, render: (p) => <span className="text-[var(--color-ink-soft)]">{p.category ?? '—'}</span> },
        { key: 'canais', label: 'Canais', render: (p) => <Channels prospect={p} /> },
        score,
        status,
      ];
    case 'aguardando':
      return [
        place,
        {
          key: 'enviado',
          label: 'Mensagem enviada',
          render: (p) => <span className="tabular whitespace-nowrap">{since(p.last_contacted_at)}</span>,
        },
        {
          key: 'retomar',
          label: 'Retomar',
          render: (p) => {
            const label = followUpLabel(p.follow_up_at);
            return label ? (
              <Badge tone={label.late ? 'warn' : 'neutral'}>{label.text}</Badge>
            ) : (
              <span className="text-[var(--color-ink-faint)]">sem data</span>
            );
          },
        },
        score,
        status,
      ];
    case 'responderam':
    case 'negociacao':
      return [
        place,
        {
          key: 'resposta',
          label: 'Respondeu',
          render: (p) => <span className="tabular whitespace-nowrap">{since(p.last_reply_at)}</span>,
        },
        {
          key: 'retomar',
          label: 'Retomar',
          wide: true,
          render: (p) => {
            const label = followUpLabel(p.follow_up_at);
            return label ? <Badge tone={label.late ? 'warn' : 'neutral'}>{label.text}</Badge> : <span className="text-[var(--color-ink-faint)]">—</span>;
          },
        },
        score,
        status,
      ];
    case 'encerrados':
      return [place, { key: 'categoria', label: 'Categoria', wide: true, render: (p) => p.category ?? '—' }, score, status];
    case 'descartados':
      return [
        place,
        {
          key: 'motivo',
          label: 'Por que saiu',
          render: (p) => <span className="text-[var(--color-ink-soft)]">{p.disqualified_reason ?? '—'}</span>,
        },
      ];
  }
}

function ProspectTable({ prospects, stage, dimmed }: { prospects: Prospect[]; stage: ProspectStage; dimmed: boolean }) {
  const columns = columnsFor(stage);
  return (
    <div className={cx('overflow-x-auto border-t border-[var(--color-line)] transition-opacity', dimmed && 'opacity-60')}>
      <table className="w-full text-sm">
        <thead>
          {/* `whitespace-nowrap`: cabeçalho quebrado em duas linhas desalinha a tabela inteira quando a janela aperta. */}
          <tr className="text-left text-xs whitespace-nowrap text-[var(--color-ink-faint)]">
            <th className="py-2.5 pr-3 pl-5 font-medium">Empresa</th>
            {columns.map((column) => (
              <th
                key={column.key}
                className={cx(
                  'py-2.5 pr-3 font-medium last:pr-5',
                  column.align === 'right' && 'text-right',
                  column.wide && 'hidden md:table-cell',
                )}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {prospects.map((prospect) => (
            <tr key={prospect.id} className="group border-t border-[var(--color-line)] hover:bg-[var(--color-hover)]">
              <td className="py-2.5 pr-3 pl-5">
                <Link
                  to={`/prospects/${prospect.id}`}
                  className="font-medium text-[var(--color-ink)] group-hover:text-[var(--color-brand-text)] hover:underline"
                >
                  {prospect.name}
                </Link>
              </td>
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cx(
                    'py-2.5 pr-3 align-middle last:pr-5',
                    column.align === 'right' && 'tabular text-right',
                    column.wide && 'hidden md:table-cell',
                  )}
                >
                  {column.render(prospect)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Os canais por onde dá para abordar, e não os valores em si.
 *
 * Numa lista de 50 empresas o telefone completo é ruído; o que decide se a
 * empresa vale o clique é "tem WhatsApp?", "tem site?".
 */
function Channels({ prospect }: { prospect: Prospect }) {
  const channels = [
    prospect.phone_e164 && 'Telefone',
    prospect.website_host && 'Site',
    prospect.instagram_username && 'Instagram',
  ].filter(Boolean) as string[];

  if (channels.length === 0) {
    return <span className="text-xs text-[var(--color-warn)]">sem contato</span>;
  }
  return <span className="text-xs whitespace-nowrap text-[var(--color-ink-soft)]">{channels.join(', ')}</span>;
}

function StatusBadge({ status }: { status: ProspectStatus }) {
  const tones: Partial<Record<ProspectStatus, 'neutral' | 'brand' | 'ok' | 'warn' | 'danger' | 'night'>> = {
    ANALYZING: 'brand',
    QUALIFIED: 'ok',
    READY_TO_CONTACT: 'brand',
    REPLIED: 'night',
    MEETING: 'night',
    PROPOSAL: 'night',
    WON: 'ok',
    LOST: 'danger',
    FOLLOW_UP: 'warn',
  };
  return <Badge tone={tones[status] ?? 'neutral'}>{PROSPECT_STATUS_LABEL[status]}</Badge>;
}

function Pagination({
  offset,
  total,
  onChange,
}: {
  offset: number;
  total: number;
  onChange: (offset: number) => void;
}) {
  if (total <= PAGE_SIZE) {
    return (
      <p className="border-t border-[var(--color-line)] px-5 py-3 text-xs text-[var(--color-ink-faint)]">
        {total} {total === 1 ? 'empresa' : 'empresas'}
      </p>
    );
  }
  const first = offset + 1;
  const last = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className="flex items-center justify-between border-t border-[var(--color-line)] px-5 py-3">
      <span className="tabular text-xs text-[var(--color-ink-soft)]">
        {first}–{last} de {total}
      </span>
      <div className="flex gap-2">
        <Button size="sm" disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - PAGE_SIZE))}>
          Anterior
        </Button>
        <Button size="sm" disabled={last >= total} onClick={() => onChange(offset + PAGE_SIZE)}>
          Próxima
        </Button>
      </div>
    </div>
  );
}

// --- Importação -------------------------------------------------------------

/**
 * Importar é em dois passos: pré-visualizar e confirmar.
 *
 * O primeiro passo (`dryRun`) diz quais colunas foram reconhecidas, quantas
 * linhas entram e quais têm problema — sem gravar nada. Importar às cegas é o
 * caminho para um banco cheio de lixo que ninguém sabe de onde veio.
 */
function ImportPanel({ onImported }: { onImported: () => void }) {
  const queryClient = useQueryClient();
  const { data: campaignsData } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => campaignsApi.list(),
  });

  const [campaignId, setCampaignId] = useState('');
  const [content, setContent] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: ({ dryRun }: { dryRun: boolean }) =>
      prospectsApi.import(campaignId, content, dryRun),
    onSuccess: (data) => {
      setError(null);
      if (data.dryRun) {
        setPreview(data);
        return;
      }
      setResult(data);
      setPreview(null);
      void queryClient.invalidateQueries({ queryKey: ['prospects'] });
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (err: unknown) => {
      setPreview(null);
      setError(err instanceof ApiError ? err.message : 'Não foi possível importar o arquivo.');
    },
  });

  function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setPreview(null);
    setResult(null);
    // Lê no navegador e manda o texto: a API recebe o CSV como corpo JSON, o
    // que evita uma dependência de upload multipart no servidor.
    void file.text().then(setContent);
  }

  const ready = campaignId !== '' && content.trim() !== '';

  return (
    <Panel title="Importar CSV" description="Pré-visualize antes de gravar: a tela mostra o que entra e o que fica de fora.">
      <div className="flex flex-col gap-3">
        <div className="grid gap-3 md:grid-cols-2">
          <Select
            label="Campanha"
            id="import-campanha"
            value={campaignId}
            onChange={(value) => {
              setCampaignId(value);
              setPreview(null);
              setResult(null);
            }}
            options={[
              { value: '', label: 'Selecione…' },
              ...(campaignsData?.campaigns ?? []).map((campaign) => ({
                value: campaign.id,
                label: campaign.name,
              })),
            ]}
          />

          <div className="flex flex-col gap-1">
            <label htmlFor="import-arquivo" className="text-xs font-medium text-[var(--color-ink-soft)]">
              Arquivo
            </label>
            <input
              id="import-arquivo"
              type="file"
              accept=".csv,text/csv,text/plain"
              onChange={handleFile}
              className="text-sm file:mr-2 file:rounded-[var(--radius-sm)] file:border file:border-[var(--color-line-strong)] file:bg-[var(--color-surface)] file:px-2 file:py-1 file:text-sm"
            />
            <span className="text-xs text-[var(--color-ink-faint)]">
              {fileName ?? 'Colunas aceitas em português ou inglês: nome, telefone, cidade, site, instagram…'}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="import-conteudo" className="text-xs font-medium text-[var(--color-ink-soft)]">
            Conteúdo
          </label>
          <textarea
            id="import-conteudo"
            rows={5}
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              setPreview(null);
              setResult(null);
            }}
            placeholder="nome,telefone,cidade&#10;Hamburgueria do João,(83) 99999-1234,João Pessoa"
            className="rounded-[var(--radius-sm)] border border-[var(--color-line-strong)] bg-[var(--color-surface)] px-3 py-2 focus:outline-none focus-visible:border-[var(--color-brand)] font-mono text-xs"
          />
        </div>

        {error && <Alert>{error}</Alert>}
        {preview && <PreviewSummary preview={preview} />}
        {result && <ImportSummary result={result} />}

        <div className="flex gap-2">
          <Button
            disabled={!ready}
            loading={mutation.isPending && mutation.variables?.dryRun === true}
            onClick={() => mutation.mutate({ dryRun: true })}
          >
            Pré-visualizar
          </Button>
          <Button
            variant="primary"
            disabled={!ready || !preview}
            loading={mutation.isPending && mutation.variables?.dryRun === false}
            onClick={() => mutation.mutate({ dryRun: false })}
          >
            Importar
          </Button>
          {result && (
            <Button variant="ghost" onClick={onImported}>
              Concluir
            </Button>
          )}
        </div>

        {!preview && !result && (
          <p className="text-xs text-[var(--color-ink-faint)]">
            A pré-visualização é obrigatória antes de gravar: ela mostra o que entra e o que fica
            de fora, sem tocar no banco.
          </p>
        )}
      </div>
    </Panel>
  );
}

function PreviewSummary({ preview }: { preview: ImportResult }) {
  return (
    <div className="flex flex-col gap-2 rounded-[var(--radius-sm)] border border-[var(--color-line)] bg-[var(--color-canvas)] p-3 text-sm">
      <p>
        <strong>{preview.valid}</strong> de {preview.total} linhas prontas para importar.
      </p>
      <p className="text-xs text-[var(--color-ink-soft)]">
        Colunas reconhecidas: {preview.mapped.join(', ') || 'nenhuma'}
        {preview.ignored.length > 0 && ` · ignoradas: ${preview.ignored.join(', ')}`}
      </p>
      {preview.preview && preview.preview.length > 0 && (
        <ul className="flex flex-col gap-0.5 text-xs text-[var(--color-ink-soft)]">
          {preview.preview.map((lead, index) => (
            <li key={`${lead.name}-${index}`}>
              {lead.name}
              {lead.city ? ` · ${lead.city}` : ''}
              {lead.phoneE164 ? ` · ${lead.phoneE164}` : ''}
              {/* As notas explicam o que a normalização reinterpretou — por
                  exemplo, "o campo site continha um Instagram". */}
              {lead.notes.length > 0 && (
                <span className="text-[var(--color-warn)]"> — {lead.notes.join(' ')}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      <ImportErrors errors={preview.errors} />
    </div>
  );
}

function ImportSummary({ result }: { result: ImportResult }) {
  const duplicatesByKey = Object.entries(result.duplicatesByKey ?? {});
  return (
    <div className="flex flex-col gap-2 rounded-[var(--radius-sm)] border-l-2 border-[var(--color-ok)] bg-[var(--color-canvas)] p-3 text-sm">
      <p>
        <strong>{result.imported}</strong> {result.imported === 1 ? 'empresa importada' : 'empresas importadas'}
        {result.duplicates ? `, ${result.duplicates} já estavam na base` : ''}.
      </p>
      {duplicatesByKey.length > 0 && (
        <p className="text-xs text-[var(--color-ink-soft)]">
          Duplicadas por: {duplicatesByKey
            .map(([key, count]) => `${MATCH_KEY_LABEL[key] ?? key} (${count})`)
            .join(', ')}
        </p>
      )}
      <ImportErrors errors={result.errors} />
    </div>
  );
}

/** Linha com problema é listada com o número: sem ele, não dá para corrigir. */
function ImportErrors({ errors }: { errors: { line: number; error: string }[] }) {
  if (errors.length === 0) return null;
  return (
    <details className="text-xs text-[var(--color-warn)]">
      <summary className="cursor-pointer">
        {errors.length} {errors.length === 1 ? 'linha com problema' : 'linhas com problema'}
      </summary>
      <ul className="mt-1 flex flex-col gap-0.5">
        {errors.slice(0, 20).map((item) => (
          <li key={item.line}>
            Linha {item.line}: {item.error}
          </li>
        ))}
        {errors.length > 20 && <li>… e mais {errors.length - 20}.</li>}
      </ul>
    </details>
  );
}
